import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Badge, Loader, TagsInput, SegmentedControl, Select, Textarea } from '@mantine/core';
import { IconMail, IconSend, IconHistory, IconCheck, IconX, IconWallet } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useSendBulkTariffsEmail, useSendBalanceEmail, useTariffEmailLogs } from '@/hooks/api-hooks';
import { formatDay, formatToLocalTime } from '@/lib/format-utils';
import DatePickerCustom from './custom-date-picker';
import { localTenant } from '@/services/session-service';

interface Props {
    business: Business;
}

export default function BusinessEmailsTab({ business }: Props): JSX.Element {
    const { t } = useTranslation();

    const tenant = localTenant();
    const senderEmails = Array.from(
        new Set([
            tenant?.settings?.email,
            ...(tenant?.settings?.sender_emails || [])
        ].filter(Boolean))
    );
    const [selectedFromEmailTariffs, setSelectedFromEmailTariffs] = useState<string>(senderEmails[0] || '');
    const [selectedFromEmailBalance, setSelectedFromEmailBalance] = useState<string>(senderEmails[0] || '');

    useEffect(() => {
        if (senderEmails.length > 0) {
            if (!selectedFromEmailTariffs) setSelectedFromEmailTariffs(senderEmails[0]);
            if (!selectedFromEmailBalance) setSelectedFromEmailBalance(senderEmails[0]);
        }
    }, [senderEmails]);

    const businessEmails = Object.values(business.emails || {}).filter(e => typeof e === 'string' && e.trim() !== '') as string[];

    // --- Tariffs Access Email State ---
    const [selectedTariffEmails, setSelectedTariffEmails] = useState<string[]>([]);
    const { mutate: sendTariffsEmail, isPending: isSendingTariffs } = useSendBulkTariffsEmail();

    // --- Reservations Payment Status Email State ---
    const [selectedBalanceEmails, setSelectedBalanceEmails] = useState<string[]>([]);
    const [manualBalanceEmails, setManualBalanceEmails] = useState<string[]>([]);
    const [paymentStatuses, setPaymentStatuses] = useState<string[]>(['paid', 'unpaid', 'partial']);
    const [dateMode, setDateMode] = useState<'all' | 'range'>('all');
    const [startDay, setStartDay] = useState<string>('');
    const [endDay, setEndDay] = useState<string>('');
    const [balanceCustomMessage, setBalanceCustomMessage] = useState<string>('');

    const sendBalanceMutation = useSendBalanceEmail();

    // --- Email Logs History ---
    const {
        data: emailLogsData,
        isLoading: isLoadingLogs,
        refetch: refetchLogs
    } = useTariffEmailLogs(business.id);

    const logs = emailLogsData?.data || [];

    const toggleTariffEmailSelection = (email: string) => {
        if (selectedTariffEmails.includes(email)) {
            setSelectedTariffEmails(selectedTariffEmails.filter(e => e !== email));
        } else {
            setSelectedTariffEmails([...selectedTariffEmails, email]);
        }
    };

    const toggleBalanceEmailSelection = (email: string) => {
        if (selectedBalanceEmails.includes(email)) {
            setSelectedBalanceEmails(selectedBalanceEmails.filter(e => e !== email));
        } else {
            setSelectedBalanceEmails([...selectedBalanceEmails, email]);
        }
    };

    const togglePaymentStatus = (status: string) => {
        if (paymentStatuses.includes(status)) {
            setPaymentStatuses(paymentStatuses.filter(s => s !== status));
        } else {
            setPaymentStatuses([...paymentStatuses, status]);
        }
    };

    const handleSendTariffs = () => {
        if (selectedTariffEmails.length === 0) return;
        sendTariffsEmail({
            businessIds: [business.id],
            manualEmails: selectedTariffEmails,
            fromEmail: selectedFromEmailTariffs
        }, {
            onSettled: () => {
                refetchLogs();
            }
        });
    };

    const handleSendBalanceStatus = () => {
        const allEmails = [...selectedBalanceEmails, ...manualBalanceEmails].filter(Boolean);
        if (allEmails.length === 0 || paymentStatuses.length === 0) return;

        sendBalanceMutation.mutate({
            clientId: business.id,
            clientType: 'business',
            emails: allEmails,
            startDay: startDay || undefined,
            endDay: endDay || undefined,
            paymentStatusFilter: paymentStatuses,
            fromEmail: selectedFromEmailBalance,
            tenantBusinessId: business.id,
            customMessage: balanceCustomMessage.trim() || undefined
        }, {
            onSuccess: () => {
                refetchLogs();
                setBalanceCustomMessage('');
            }
        });
    };

    return (
        <div className="space-y-6 max-w-3xl">
            {/* Section 1: Send Tariffs Access Email */}
            <div className="solid-box-border space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                    <IconMail size={20} className="text-gray-700 shrink-0" />
                    <div>
                        <div className="text-md font-bold text-gray-900">
                            {t('send_tariffs_email')}
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    {senderEmails.length > 0 && (
                        <Select
                            label={t('select_sender_email')}
                            data={senderEmails}
                            value={selectedFromEmailTariffs}
                            onChange={(val) => setSelectedFromEmailTariffs(val || '')}
                            allowDeselect={false}
                            size="xs"
                        />
                    )}

                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">{t('select_recipient_emails')}</span>
                        {/* {businessEmails.length > 0 && (
                            <Button
                                variant="light"
                                color="gray"
                                size="compact-xs"
                                onClick={() => {
                                    if (selectedTariffEmails.length === businessEmails.length) {
                                        setSelectedTariffEmails([]);
                                    } else {
                                        setSelectedTariffEmails([...businessEmails]);
                                    }
                                }}
                            >
                                {selectedTariffEmails.length === businessEmails.length ? t('deselect_all') : t('select_all')}
                            </Button>
                        )} */}
                    </div>

                    {businessEmails.length === 0 ? (
                        <div className="text-xs text-gray-500 italic py-2">
                            {t('no_emails_configured_for_this_business')}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            {businessEmails.map(email => {
                                const isSelected = selectedTariffEmails.includes(email);
                                return (
                                    <div
                                        key={email}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer select-none ${isSelected ? 'bg-gray-100/80 border-gray-300' : 'bg-gray-50/50 border-gray-100 hover:bg-gray-100/40'}`}
                                        onClick={() => toggleTariffEmailSelection(email)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => { }}
                                            size="xs"
                                            color="black"
                                        />
                                        <span className="text-xs font-semibold text-gray-800">{email}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            leftSection={<IconSend size={14} />}
                            onClick={handleSendTariffs}
                            loading={isSendingTariffs}
                            disabled={selectedTariffEmails.length === 0}
                        >
                            {t('send')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Section 2: Send Reservations Payment Status Email */}
            <div className="solid-box-border space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                    <IconWallet size={20} className="text-gray-700 shrink-0" />
                    <div>
                        <div className="text-md font-bold text-gray-900">
                            {t('send_reservations_payment_status_email')}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    {senderEmails.length > 0 && (
                        <Select
                            label={t('select_sender_email')}
                            data={senderEmails}
                            value={selectedFromEmailBalance}
                            onChange={(val) => setSelectedFromEmailBalance(val || '')}
                            allowDeselect={false}
                            size="xs"
                        />
                    )}

                    {/* Status Selection */}
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            {[
                                { value: 'paid', label: t('paid') },
                                { value: 'unpaid', label: t('unpaid') },
                                { value: 'partial', label: t('partially_paid') },
                            ].map(item => {
                                const isSelected = paymentStatuses.includes(item.value);
                                return (
                                    <div
                                        key={item.value}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors cursor-pointer select-none ${isSelected ? 'bg-gray-100/90 border-gray-300 font-semibold' : 'bg-gray-50/50 border-gray-100 hover:bg-gray-100/40 text-gray-400'}`}
                                        onClick={() => togglePaymentStatus(item.value)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => { }}
                                            size="xs"
                                            color="black"
                                        />
                                        <span className={`text-xs font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-400'}`}>{item.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Optional Date Range */}
                    {/* Date Scope Selection: All History vs Select Dates */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                                {t('balance_dates')}
                            </span>
                        </div>
                        <SegmentedControl
                            value={dateMode}
                            onChange={(val) => {
                                const mode = val as 'all' | 'range';
                                setDateMode(mode);
                                if (mode === 'all') {
                                    setStartDay('');
                                    setEndDay('');
                                }
                            }}
                            data={[
                                { label: t('all_history'), value: 'all' },
                                { label: t('select_dates'), value: 'range' },
                            ]}
                            size="xs"
                            radius="md"
                            className="w-full"
                        />

                        {dateMode === 'range' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                <div>
                                    <span className="block text-xs font-semibold text-gray-700 mb-1">
                                        {t('from_date')}
                                    </span>
                                    <DatePickerCustom
                                        value={startDay}
                                        onChange={([start]) => setStartDay(start)}
                                        size="sm"
                                        hideLabel
                                    />
                                </div>
                                <div>
                                    <span className="block text-xs font-semibold text-gray-700 mb-1">
                                        {t('to_date')}
                                    </span>
                                    <DatePickerCustom
                                        value={endDay}
                                        onChange={([start]) => setEndDay(start)}
                                        size="sm"
                                        hideLabel
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Recipient Emails */}
                    <div>
                        {businessEmails.length > 0 && (
                            <div className="flex flex-col gap-1.5 mb-3">
                                {businessEmails.map(email => {
                                    const isSelected = selectedBalanceEmails.includes(email);
                                    return (
                                        <div
                                            key={email}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer select-none ${isSelected ? 'bg-gray-100/80 border-gray-300' : 'bg-gray-50/50 border-gray-100 hover:bg-gray-100/40'}`}
                                            onClick={() => toggleBalanceEmailSelection(email)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => { }}
                                                size="xs"
                                                color="black"
                                            />
                                            <span className="text-xs font-semibold text-gray-800">{email}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div>
                            <span className="block text-xs font-semibold text-gray-700 mb-1">
                                {t('enter_additional_emails')}
                            </span>
                            <TagsInput
                                placeholder="email@domain.com"
                                size="xs"
                                value={manualBalanceEmails}
                                onChange={setManualBalanceEmails}
                                clearable
                            />
                        </div>

                        <div>
                            <span className="block text-xs font-semibold text-gray-700 mb-1">
                                {t('custom_message_optional')}
                            </span>
                            <Textarea
                                placeholder={t('custom_message_placeholder')}
                                size="xs"
                                value={balanceCustomMessage}
                                onChange={(e) => setBalanceCustomMessage(e.currentTarget.value)}
                                minRows={2}
                                maxRows={5}
                                autosize
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            leftSection={<IconSend size={14} />}
                            onClick={handleSendBalanceStatus}
                            loading={sendBalanceMutation.isPending}
                            disabled={(selectedBalanceEmails.length === 0 && manualBalanceEmails.length === 0) || paymentStatuses.length === 0}
                        >
                            {t('send')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Section 3: Sent Email Logs History */}
            <div className="solid-box-border">
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <IconHistory size={18} className="text-gray-700" />
                        <div className="text-md font-bold text-gray-900">
                            {t('sent_email_history')}
                        </div>
                    </div>
                    <Badge variant="light" color="gray" size="sm">
                        {logs.length}
                    </Badge>
                </div>

                {isLoadingLogs ? (
                    <div className="flex justify-center py-6">
                        <Loader size="sm" color="black" />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-xs text-gray-500 py-4 text-center">
                        {t('no_emails_sent_yet')}
                    </div>
                ) : (
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {logs.map((log: any) => {
                            const statusStr = String(log.status || '').toLowerCase();
                            const isSuccess = statusStr === 'sent' || statusStr === 'success';
                            const isFailed = statusStr.includes('failed') || statusStr.includes('error');
                            const recipient = log.sent_to || log.recipient_email || '—';
                            const templateName = log.template_used || log.template_name || t('send_email');
                            const formattedDate = formatDay(log.created_at);
                            const formattedTime = formatToLocalTime(log.created_at);

                            return (
                                <div
                                    key={log.id}
                                    className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3 text-xs hover:bg-gray-100/40 transition-colors"
                                >
                                    <div className="space-y-1.5 min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-gray-900 truncate">
                                                {templateName}
                                            </span>
                                            {isSuccess && (
                                                <Badge color="green" size="xs" variant="light" radius="sm">
                                                    <IconCheck size={10} className="mr-1 inline" />
                                                    {t('sent')}
                                                </Badge>
                                            )}
                                            {isFailed && (
                                                <Badge color="red" size="xs" variant="light" radius="sm">
                                                    <IconX size={10} className="mr-1 inline" />
                                                    {t('failed')}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-gray-500 text-[11px] truncate">
                                            {t('sent_to')}: <span className="font-semibold text-gray-700">{recipient}</span>
                                        </div>
                                    </div>
                                    <div className="text-right text-[11px] text-gray-400 shrink-0">
                                        <div>{formattedDate}</div>
                                        <div>{formattedTime}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
